import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Lightweight i18n. Two languages: English (default) and Czech.
 *
 * Usage:
 *   const { t, locale, setLocale } = useLocale();
 *   <button>{t("nav.dashboard")}</button>
 *
 * Falls back to English if a key is missing in Czech.
 * Persists choice in localStorage so it survives reloads.
 */
const STORE_KEY = "fs_locale";

const STRINGS = {
    en: {
        // Navigation
        "nav.dashboard": "Dashboard",
        "nav.research": "Research",
        "nav.library": "Library",
        "nav.synergy": "Synergy",
        "nav.network": "Network",
        "nav.board": "Board",
        "nav.patterns": "Patterns",
        "nav.workspace": "Workspace",

        // Top toolbar
        "toolbar.download": "Download",
        "toolbar.print": "Print",
        "toolbar.building": "Building…",
        "toolbar.language": "Language",

        // Common actions
        "action.save": "Save",
        "action.cancel": "Cancel",
        "action.delete": "Delete",
        "action.search": "Search",
        "action.research": "Research",
        "action.analyze": "Analyze",
        "action.find_partners": "Find partners",
        "action.draft_email": "Intro email",
        "action.pitch_deck": "Pitch deck",
        "action.markdown": "Markdown",
        "action.refresh": "Refresh",
        "action.copy": "Copy",
        "action.close": "Close",

        // Dashboard
        "dashboard.title": "Pipeline overview",
        "dashboard.velocity": "Pipeline velocity",
        "dashboard.by_theme": "Pipeline by theme",
        "dashboard.top_opportunities": "Top opportunities",
        "dashboard.top_firms": "Top firms by revenue",
        "dashboard.recent_firms": "Recently added firms",
        "dashboard.stage_exploring": "Exploring",
        "dashboard.stage_contacted": "Contacted",
        "dashboard.stage_meeting": "Meeting",
        "dashboard.stage_closed": "Closed",
        "dashboard.synergies": "synergies",
        "dashboard.synergy": "synergy",
        "dashboard.throughput": "moved past Exploring in the last 7 days",
        "dashboard.projected": "projected",

        // Library
        "library.title": "Firm library",
        "library.empty": "No firms yet — research one to get started.",
        "library.search_placeholder": "Search firms…",

        // Research
        "research.title": "Research a firm",
        "research.placeholder": "Company name (e.g. Stripe)",
        "research.run": "Run research",
        "research.searching": "Researching…",

        // Synergy
        "synergy.title": "Cross-sell synergy",
        "synergy.firm_a": "Source firm (sells)",
        "synergy.firm_b": "Target firm (buys)",
        "synergy.auto_best": "Auto-pick best product",
        "synergy.run": "Analyze fit",
        "synergy.fit": "Fit",
        "synergy.projected_revenue": "Projected revenue",
        "synergy.mutual_fit": "Mutual fit",
        "synergy.bilateral_fit": "Bilateral fit",

        // Network
        "network.title": "Library network graph",
        "network.tag_themes": "Tag themes",
        "network.tagging": "Tagging…",
        "network.color_region": "Region",
        "network.color_themes": "Themes",
        "network.color_firm": "Firm",
        "network.color_multi": "Multi",
        "network.hot_opportunity": "Hot opportunity",

        // Board
        "board.title": "Action board",
        "board.group_by_theme": "Group by theme",
        "board.swimlanes_on": "Swimlanes: on",
        "board.stale": "stale",
        "board.empty": "Empty",

        // Auth
        "auth.email": "Email",
        "auth.password": "Password",
        "auth.sign_in": "Sign in",
        "auth.sign_up": "Sign up",
        "auth.logout": "Log out",
    },
    cs: {
        // Navigation
        "nav.dashboard": "Přehled",
        "nav.research": "Výzkum",
        "nav.library": "Knihovna",
        "nav.synergy": "Synergie",
        "nav.network": "Síť",
        "nav.board": "Tabule",
        "nav.patterns": "Šablony",
        "nav.workspace": "Pracovní prostor",

        // Top toolbar
        "toolbar.download": "Stáhnout",
        "toolbar.print": "Tisk",
        "toolbar.building": "Připravuji…",
        "toolbar.language": "Jazyk",

        // Common actions
        "action.save": "Uložit",
        "action.cancel": "Zrušit",
        "action.delete": "Smazat",
        "action.search": "Hledat",
        "action.research": "Prozkoumat",
        "action.analyze": "Analyzovat",
        "action.find_partners": "Najít partnery",
        "action.draft_email": "Nabídkový e-mail",
        "action.pitch_deck": "Prezentace",
        "action.markdown": "Markdown",
        "action.refresh": "Obnovit",
        "action.copy": "Kopírovat",
        "action.close": "Zavřít",

        // Dashboard
        "dashboard.title": "Přehled obchodního trychtýře",
        "dashboard.velocity": "Rychlost trychtýře",
        "dashboard.by_theme": "Trychtýř podle tématu",
        "dashboard.top_opportunities": "Top příležitosti",
        "dashboard.top_firms": "Top firmy podle obratu",
        "dashboard.recent_firms": "Nedávno přidané firmy",
        "dashboard.stage_exploring": "Průzkum",
        "dashboard.stage_contacted": "Kontaktováno",
        "dashboard.stage_meeting": "Schůzka",
        "dashboard.stage_closed": "Uzavřeno",
        "dashboard.synergies": "synergií",
        "dashboard.synergy": "synergie",
        "dashboard.throughput": "se za posledních 7 dní posunulo z Průzkumu dál",
        "dashboard.projected": "projektováno",

        // Library
        "library.title": "Knihovna firem",
        "library.empty": "Zatím žádné firmy — začněte výzkumem.",
        "library.search_placeholder": "Hledat firmy…",

        // Research
        "research.title": "Prozkoumat firmu",
        "research.placeholder": "Název firmy (např. Stripe)",
        "research.run": "Spustit výzkum",
        "research.searching": "Hledám…",

        // Synergy
        "synergy.title": "Cross-sell synergie",
        "synergy.firm_a": "Zdrojová firma (prodává)",
        "synergy.firm_b": "Cílová firma (kupuje)",
        "synergy.auto_best": "Vybrat nejlepší produkt automaticky",
        "synergy.run": "Analyzovat shodu",
        "synergy.fit": "Shoda",
        "synergy.projected_revenue": "Projektovaný obrat",
        "synergy.mutual_fit": "Vzájemná shoda",
        "synergy.bilateral_fit": "Oboustranný zájem",

        // Network
        "network.title": "Síťový graf knihovny",
        "network.tag_themes": "Otagovat témata",
        "network.tagging": "Taguji…",
        "network.color_region": "Region",
        "network.color_themes": "Témata",
        "network.color_firm": "Firma",
        "network.color_multi": "Vše",
        "network.hot_opportunity": "Žhavá příležitost",

        // Board
        "board.title": "Akční tabule",
        "board.group_by_theme": "Seskupit podle tématu",
        "board.swimlanes_on": "Dráhy: zapnuto",
        "board.stale": "uvíznuté",
        "board.empty": "Prázdné",

        // Auth
        "auth.email": "E-mail",
        "auth.password": "Heslo",
        "auth.sign_in": "Přihlásit",
        "auth.sign_up": "Registrace",
        "auth.logout": "Odhlásit",
    },
};

const LocaleContext = createContext({ locale: "en", t: (k) => k, setLocale: () => {} });

export function LocaleProvider({ children }) {
    const [locale, setLocaleState] = useState(() => {
        try { return localStorage.getItem(STORE_KEY) || "en"; } catch { return "en"; }
    });
    useEffect(() => {
        try { localStorage.setItem(STORE_KEY, locale); } catch { /* private mode */ }
        document.documentElement.setAttribute("lang", locale);
    }, [locale]);

    const setLocale = useCallback((next) => setLocaleState(next === "cs" ? "cs" : "en"), []);

    const t = useCallback((key) => {
        const dict = STRINGS[locale] || STRINGS.en;
        return dict[key] || STRINGS.en[key] || key;
    }, [locale]);

    return <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
    return useContext(LocaleContext);
}
